/**
 * useCompactMode — tracks whether the viewport is in compact (mobile) layout.
 *
 * Single source of truth for the 899px breakpoint that the PMS dashboard and
 * every admin surface (shell + pages) switch on. Previously this hook +
 * `safeMatchMedia` + the `COMPACT_MEDIA_QUERY` constant were copy-pasted into
 * nine files; this is the shared extraction (SCRUM-127 mobile pass).
 *
 * `override` forces a value — used by tests (and SSR) so a component can be
 * rendered in compact or expanded mode deterministically.
 */
import { useState, useEffect } from 'react';

/** Viewport width at/below which the UI switches to the compact (mobile) layout. */
export const COMPACT_MEDIA_QUERY = '(max-width: 899px)';

/**
 * Defensive `matchMedia` access — JSDOM doesn't polyfill it, so tests would
 * crash without this guard. Real browsers always have it.
 */
function safeMatchMedia(query: string): MediaQueryList | null {
  if (typeof window === 'undefined') return null;
  if (typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}

export function useCompactMode(override?: boolean): boolean {
  const [compact, setCompact] = useState<boolean>(() => {
    if (override !== undefined) return override;
    return safeMatchMedia(COMPACT_MEDIA_QUERY)?.matches ?? false;
  });
  useEffect(() => {
    if (override !== undefined) {
      setCompact(override);
      return undefined;
    }
    const mq = safeMatchMedia(COMPACT_MEDIA_QUERY);
    if (mq === null) return undefined;
    const onChange = (e: MediaQueryListEvent): void => setCompact(e.matches);
    mq.addEventListener('change', onChange);
    setCompact(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [override]);
  return compact;
}
